import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * Capability booleans for the Student surfaces (list + card), derived from the
 * current user's roles. Mirrors `useGroupPermissions`. Hiding a control is a UX
 * affordance only — the backend remains the authority on each mutation/export.
 */
export interface IStudentPermissions {
  /** Admin, FinanceManager, AccountManager (SalesManager: read). */
  canViewFinance: boolean;
  /** Same set as `canViewFinance` — gates ALL export controls (Req 21.5). */
  canExportFinance: boolean;
  /** Admin, FinanceManager, AccountManager. */
  canRecordPayment: boolean;
  /** Admin, FinanceManager, AccountManager. */
  canIssueInvoice: boolean;
  /** Admin, AccountManager, SalesManager. */
  canAddAgreement: boolean;
  /** Admin, AccountManager, SalesManager. */
  canChangeGroup: boolean;
  /** Admin, AccountManager, SalesManager. */
  canContactEmail: boolean;
  /** Admin, FinanceManager, AccountManager, SalesManager — block LMS access. */
  canBlockLmsAccess: boolean;
}

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * Reads `userStateVar().me.roles` and maps `UserRole` to capability booleans
 * for the Student surfaces.
 */
export const useStudentPermissions = (): IStudentPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canViewFinance = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  const canRecordPayment = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
  ]);

  const canManageRelationship = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  // Block LMS access — same set the backend accepts (PAYMENT_ACCESS_ROLES:
  // Admin, FinanceManager, AccountManager, SalesManager).
  const canBlockLmsAccess = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  return {
    canViewFinance,
    canExportFinance: canViewFinance,
    canRecordPayment,
    canIssueInvoice: canRecordPayment,
    canAddAgreement: canManageRelationship,
    canChangeGroup: canManageRelationship,
    canContactEmail: canManageRelationship,
    canBlockLmsAccess,
  };
};
